import { useId } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import type { PayoutStatementView } from '@/api/generated';
import { TotalsFooter } from '@/components/cells';
import { DefinitionField, DefinitionList } from '@/components/DefinitionList';
import { formatSignedMoney, moneyIn, moneyMicroIn } from '@/components/moneyIn';
import { formatCivilOrDash, formatInstantOrDash } from '@/i18n/format';
import { enumLabel } from '@/i18n/enumLabel';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme';
import { downloadCsv } from '@/utils/csv';
import { ledgerFilename, toCsv } from './ledgerCsv';
import { PayoutStatus } from './PayoutTable';
import { splitOf } from './payoutStatus';
import {
  affiliateProgrammeOf,
  groupByPayment,
  isSyntheticAffiliateLine,
  settlementMonth,
} from './statementMath';

export interface LedgerDrillInProps {
  statement: PayoutStatementView;
  /** Closes the ledger (the settlement leaves the URL). */
  onClose?: () => void;
}

/** When a line was collected: the instant, else the civil day it names. */
function collectedLabel(value: string | undefined, locale: string, fallback: string): string {
  return formatInstantOrDash(value, locale, '') || formatCivilOrDash(value, locale, fallback);
}

export function LedgerDrillIn({ statement, onClose }: LedgerDrillInProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  // Each payment's table is named by its visible reference, the affiliate table by its caption.
  const tableId = useId();
  const dash = t('statements.unavailable');
  const currency = statement.currency;
  const money = (minor: number) => moneyIn(minor, currency, locale, dash);
  const split = splitOf(statement);
  const lines = statement.lines ?? [];
  const groups = groupByPayment(lines.filter((line) => !isSyntheticAffiliateLine(line)));
  const syntheticLines = lines.filter(isSyntheticAffiliateLine);
  const commissionTotal = syntheticLines.reduce(
    (sum, line) => sum + line.affiliateCommissionMinor,
    0,
  );
  const byLine = statement.byLine ?? [];
  // The affiliate programme's lines (TMMAPI-0105), each only when sent; signs explicit (DATA-005).
  const programme = affiliateProgrammeOf(statement);
  const debit = (minor: number) => moneyIn(minor === 0 ? 0 : -Math.abs(minor), currency, locale, dash);
  const programmeItems = [
    { key: 'affiliateBounties', minor: programme.bountiesMinor, show: debit },
    { key: 'affiliateMilestones', minor: programme.milestonesMinor, show: debit },
    {
      key: 'affiliateRecovered',
      minor: programme.recoveredMinor,
      show: (minor: number) => formatSignedMoney(minor, currency, locale, dash),
    },
  ].filter((item) => item.minor !== null);

  function handleExport() {
    downloadCsv(ledgerFilename(statement), toCsv(statement));
  }

  return (
    <Paper sx={{ overflow: 'hidden', mt: 2 }}>
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: '1px solid',
          borderColor: tokens.line,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="subtitle1" component="h2" sx={{ flex: 1 }}>
          {t('statements.ledger.titleMonth', {
            month: settlementMonth(statement.periodStart, locale, dash),
            currency: currency || dash,
          })}
        </Typography>
        <Button onClick={handleExport}>{t('statements.exportCsv')}</Button>
        {onClose ? <Button onClick={onClose}>{t('payouts.closeLedger')}</Button> : null}
      </Box>
      {split ? (
        <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: tokens.line }}>
          <DefinitionList>
            <DefinitionField label={t('payouts.split.retained')}>
              <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                {split.retained.netMinor === undefined ? dash : money(split.retained.netMinor)}
              </Box>
            </DefinitionField>
            <DefinitionField label={t('payouts.split.normal')}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                  {split.normal.netMinor === undefined ? dash : money(split.normal.netMinor)}
                </Box>
                <PayoutStatus
                  stmt={statement}
                  status={split.normal.status}
                  netMinor={split.normal.netMinor}
                  locale={locale}
                />
              </Box>
            </DefinitionField>
          </DefinitionList>
          <Typography variant="caption" component="p" sx={{ color: tokens.text2, mt: 1 }}>
            {t('payouts.retainedNote')}
          </Typography>
        </Box>
      ) : null}
      {programmeItems.length > 0 ? (
        <Box
          sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: tokens.line }}
        >
          <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
            {t('statements.affiliateProgramme')}
          </Typography>
          <DefinitionList>
            {programmeItems.map((item) => (
              <DefinitionField key={item.key} label={t(`statements.${item.key}`)}>
                <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                  {item.show(item.minor ?? 0)}
                </Box>
              </DefinitionField>
            ))}
          </DefinitionList>
          <Typography variant="caption" component="p" sx={{ color: tokens.text2, mt: 1 }}>
            {t('statements.affiliateProgrammeNote')}
          </Typography>
        </Box>
      ) : null}
      {groups.map((group, groupIndex) => (
        <Box key={group.paymentRef || 'none'} sx={{ px: 1, pb: 1, overflowX: 'auto' }}>
          <Typography
            variant="mono"
            id={`${tableId}-payment-${groupIndex}`}
            sx={{ px: 1.5, pt: 1.5, display: 'block', color: tokens.text2 }}
          >
            {group.paymentRef || dash}
          </Typography>
          <Table size="small" aria-labelledby={`${tableId}-payment-${groupIndex}`}>
            <TableHead>
              <TableRow>
                <TableCell>{t('statements.ledger.kind')}</TableCell>
                <TableCell>{t('statements.ledger.line')}</TableCell>
                <TableCell>{t('statements.ledger.collected')}</TableCell>
                <TableCell align="right">{t('statements.col.retail')}</TableCell>
                <TableCell align="right">{t('statements.col.wholesale')}</TableCell>
                <TableCell align="right">{t('statements.col.margin')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {group.lines.map((line) => (
                <TableRow key={line.id ?? `${line.collectedAt}-${line.line}`}>
                  <TableCell>
                    {line.kind ? enumLabel(t, 'statements.kind', line.kind) : dash}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {line.line ? enumLabel(t, 'statements.line', line.line) : dash}
                    {line.line === 'AI' && typeof line.aiAtOurPriceMicro === 'number' ? (
                      <Typography
                        variant="caption"
                        component="div"
                        sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}
                      >
                        {t('statements.tenant.aiAtPlatformPrice', {
                          amount: moneyMicroIn(line.aiAtOurPriceMicro, currency, locale, dash),
                        })}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    {collectedLabel(line.collectedAt, locale, dash)}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {money(line.retailMinor)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ whiteSpace: 'nowrap', color: tokens.text2 }}
                  >
                    {money(line.wholesaleMinor)}
                  </TableCell>
                  {/* Optional: an omitted margin is the dash. Retail and wholesale are always sent. */}
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {line.resellerMarginMinor === undefined
                      ? dash
                      : money(line.resellerMarginMinor)}
                    {line.affiliateCommissionMinor > 0 ? (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          color: tokens.text2,
                          fontWeight: 400,
                          whiteSpace: 'normal',
                        }}
                      >
                        {t('statements.lineCommission', {
                          amount: money(line.affiliateCommissionMinor),
                        })}
                      </Typography>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TotalsFooter>
              <TableCell component="th" scope="row" colSpan={3}>
                {t('statements.ledger.total')}
              </TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                {money(group.retail)}
              </TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                {money(group.wholesale)}
              </TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                {group.hasMargin ? money(group.margin) : dash}
              </TableCell>
            </TotalsFooter>
          </Table>
          {group.excludesMissing ? (
            <Typography variant="caption" component="p" sx={{ px: 1.5, color: 'text.secondary' }}>
              {t('common.totalExcludesMissing')}
            </Typography>
          ) : null}
        </Box>
      ))}
      {syntheticLines.length > 0 ? (
        <>
        <Box sx={{ px: 1, pb: 1, overflowX: 'auto' }}>
          <Typography
            variant="caption"
            id={`${tableId}-affiliate`}
            sx={{ px: 1.5, pt: 1.5, display: 'block', color: tokens.text2 }}
          >
            {t('statements.affiliateGroup')}
          </Typography>
          <Table size="small" aria-labelledby={`${tableId}-affiliate`}>
            <TableHead>
              <TableRow>
                <TableCell>{t('statements.ledger.kind')}</TableCell>
                <TableCell>{t('statements.ledger.line')}</TableCell>
                <TableCell>{t('statements.ledger.collected')}</TableCell>
                <TableCell align="right">{t('statements.col.retail')}</TableCell>
                <TableCell align="right">{t('statements.col.wholesale')}</TableCell>
                <TableCell align="right">{t('statements.col.margin')}</TableCell>
                {/* The commission itself — never the retired "Reseller share" (TMMRSL-0048). */}
                <TableCell align="right">{t('statements.ledger.affiliateCommission')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {syntheticLines.map((line) => (
                <TableRow key={line.id ?? `${line.collectedAt}-affiliate`}>
                  <TableCell>{dash}</TableCell>
                  <TableCell>{dash}</TableCell>
                  {/* The commission's date is what tells these rows apart. */}
                  <TableCell component="th" scope="row">
                    {collectedLabel(line.collectedAt, locale, dash)}
                  </TableCell>
                  <TableCell align="right">
                    {dash}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: tokens.text2 }}
                  >
                    {dash}
                  </TableCell>
                  <TableCell align="right">{dash}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>
                    {money(line.affiliateCommissionMinor)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TotalsFooter>
              <TableCell component="th" scope="row" colSpan={3}>
                {t('statements.ledger.total')}
              </TableCell>
              <TableCell align="right">{dash}</TableCell>
              <TableCell align="right">{dash}</TableCell>
              <TableCell align="right">{dash}</TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                {money(commissionTotal)}
              </TableCell>
            </TotalsFooter>
          </Table>
        </Box>
        </>
      ) : null}
      {byLine.length > 0 ? (
        <Box sx={{ px: 2.5, pt: 2, overflowX: 'auto' }}>
          <Typography variant="subtitle1" component="h3" sx={{ mb: 1 }}>
            {t('statements.byLine.title')}
          </Typography>
          <Table size="small" aria-label={t('statements.byLine.title')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('statements.byLine.col.line')}</TableCell>
                <TableCell align="right">{t('statements.col.retail')}</TableCell>
                <TableCell align="right">{t('statements.col.wholesale')}</TableCell>
                <TableCell align="right">{t('statements.col.margin')}</TableCell>
                <TableCell align="right">{t('statements.ledger.affiliateCommission')}</TableCell>
                <TableCell align="right">{t('statements.byLine.col.aiAtPlatformPrice')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {byLine.map((row, index) => (
                <TableRow key={row.line ?? `line-${index}`}>
                  <TableCell component="th" scope="row">
                    {row.line ? enumLabel(t, 'statements.line', row.line) : dash}
                  </TableCell>
                  {[
                    row.retailMinor,
                    row.wholesaleMinor,
                    row.resellerMarginMinor,
                    row.affiliateCommissionMinor,
                  ].map((minor, cell) => (
                    <TableCell key={cell} align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {minor === undefined ? dash : money(minor)}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {row.line === 'AI' && typeof row.aiAtOurPriceMicro === 'number'
                      ? moneyMicroIn(row.aiAtOurPriceMicro, currency, locale, dash)
                      : dash}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : null}
      <Typography variant="body2" sx={{ px: 2.5, py: 2, color: tokens.text2 }}>
        {t('statements.ledgerNote')}
      </Typography>
    </Paper>
  );
}
